import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { EXTERNAL_CODING_REFERENCE, SYNCABLE_META } from "./common";

export const RXDB_SYMPTOM_COLLECTION_NAME = "symptom";

export const useRxCollectionSymptom = (): RxCollection<Symptom> | null =>
  useRxCollection<Symptom>(RXDB_SYMPTOM_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<Symptom>>;
export type SymptomQueryConstructor = UseRxData["1"];

const SYMPTOM_SCHEMA_LITERAL = {
  title: "symptom schema",
  version: 1,
  description: "Represents a symptom",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    name: {
      type: "string",
    },
    externalCoding: {
      type: "object",
      properties: {
        icd10: EXTERNAL_CODING_REFERENCE,
        icd11: EXTERNAL_CODING_REFERENCE,
        snomed: EXTERNAL_CODING_REFERENCE,
        loinc: EXTERNAL_CODING_REFERENCE,
      },
    },
    i18n: {
      type: "array",
      items: {
        type: "object",
        properties: {
          locale: {
            type: "string",
            enum: ["en", "fr", "es", "ar", "sw", "kam"],
          },
          displayName: {
            type: "string",
          },
        },
      },
    },
    meta: SYNCABLE_META,
  },
  // https://elephant.slab.com/posts/offline-schema-field-encryption-policy-uwb0pbii#hsdou-symptom
  required: ["id", "name", "externalCoding", "i18n", "meta"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(SYMPTOM_SCHEMA_LITERAL);

export type Symptom = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const SYMPTOM_SCHEMA: RxJsonSchema<Symptom> = SYMPTOM_SCHEMA_LITERAL;
